import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ChevronLeft, ArrowRight, Layers, DollarSign, TrendingUp, Users } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import { OnboardingLayout, STARTUP_ONBOARDING_DISPLAY_STEPS } from '@/components/OnboardingLayout';
import { OnboardingSignOut } from '@/components/OnboardingSignOut';
import AnimatedPressable from '@/components/AnimatedPressable';
import { handleError, reportError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { track, useTrackScreen } from '@/lib/analytics';
import { useAuth, supabase, isSupabaseConfigured } from '@/lib/supabase';
import { buildEssentialsUpdate, type RaisingStatus } from '@/lib/supabase/profile-mappers';
import { persistOnboardingStep } from '@/lib/onboarding/persist-step';
import { persistEssentials } from '@/lib/startup/persist-essentials';
import { nextStep } from '@/lib/onboarding/steps';
import { useEntrance } from '@/lib/a11y/reduced-motion';

const STAGES = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b+', label: 'Series B+' },
] as const;

type StageValue = (typeof STAGES)[number]['value'];

const RAISING_OPTIONS: { value: RaisingStatus; label: string }[] = [
  { value: 'raising', label: "Yes, we're raising" },
  { value: 'not_raising', label: 'Not right now' },
  { value: 'not_yet', label: 'Not yet' },
];

const NEXT_ROUTE = '/(onboarding)/data-input-choice' as const;

// Year picker range — current year back 40 years. A founding year outside this
// window is rare enough that a free-text fallback isn't worth the friction.
const CURRENT_YEAR = new Date().getFullYear();
const FOUNDED_YEARS: number[] = Array.from({ length: 41 }, (_, i) => CURRENT_YEAR - i);

/** Pure — no closure over component state. Hoisted to module scope. */
function formatCurrency(value: string): string {
  const num = value.replace(/[^0-9]/g, '');
  if (!num) return '';
  return Number(num).toLocaleString();
}

/**
 * The "essentials" onboarding step — stage + fundraising intent + founded year
 * + team size (Task 3).
 *
 * `stage` and `raising` are REQUIRED (confirm/correct the prefill); funding
 * target, founded year and team size are OPTIONAL. Nothing is silently
 * defaulted: every field starts unset (or prefilled from the registry/deck)
 * and only flows into `buildEssentialsUpdate` (which omits anything still
 * unset) when the user actually provides it — a 'pre-seed'/$0/current-year/
 * team-of-1 default previously polluted score inputs.
 */
export default function CompanyEssentialsScreen() {
  useTrackScreen('CompanyEssentials');
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { isDesktop } = useResponsive();
  const { user } = useAuth();

  const startupProfile = useProfileStore((s) => s.startupProfile);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);

  // Prefilled from an existing profile (deck extraction / registry enrichment
  // may already have set values) but NEVER hardcoded-defaulted.
  const [stage, setStage] = useState<StageValue | undefined>(
    (startupProfile?.stage as StageValue | undefined) || undefined
  );
  const [raisingStatus, setRaisingStatus] = useState<RaisingStatus | undefined>(
    startupProfile?.isActivelyRaising === true ? 'raising' : undefined
  );
  const [fundingTargetInput, setFundingTargetInput] = useState(
    startupProfile?.fundingTarget ? startupProfile.fundingTarget.toLocaleString() : ''
  );
  const [foundedYear, setFoundedYear] = useState<number | undefined>(
    startupProfile?.foundedYear || undefined
  );
  const [teamSizeInput, setTeamSizeInput] = useState(
    startupProfile?.teamSize ? String(startupProfile.teamSize) : ''
  );
  const [errors, setErrors] = useState<{ stage?: boolean; raising?: boolean }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { error: showError } = useAlert();
  const entrance = useEntrance();

  const parsedFundingTarget =
    raisingStatus === 'raising' && fundingTargetInput
      ? Number(fundingTargetInput.replace(/[^0-9]/g, '')) || null
      : null;
  const parsedTeamSize = teamSizeInput ? Number(teamSizeInput.replace(/[^0-9]/g, '')) || null : null;

  const advance = async () => {
    if (user) {
      const step = nextStep('essentials', false);
      if (step) {
        try {
          await persistOnboardingStep(user.id, step);
        } catch (err) {
          reportError(err, { context: 'CompanyEssentials.advance', level: 'warning' });
        }
      }
    }
    router.push(NEXT_ROUTE);
  };

  const handleContinue = async () => {
    // stage + raising are required (confirm/correct the prefill).
    const nextErrors = { stage: !stage, raising: !raisingStatus };
    if (nextErrors.stage || nextErrors.raising) {
      setErrors(nextErrors);
      return;
    }

    setSaveError(null);
    setIsSaving(true);

    const update = buildEssentialsUpdate({
      stage,
      raisingStatus,
      fundingTarget: parsedFundingTarget,
      foundedYear: foundedYear ?? null,
      teamSize: parsedTeamSize,
    });

    if (startupProfile) {
      setStartupProfile({
        ...startupProfile,
        ...(stage ? { stage: stage as 'pre-seed' | 'seed' | 'series-a' } : {}),
        ...(parsedFundingTarget != null ? { fundingTarget: parsedFundingTarget } : {}),
        isActivelyRaising: raisingStatus === 'raising',
        ...(foundedYear != null ? { foundedYear } : {}),
        ...(parsedTeamSize != null ? { teamSize: parsedTeamSize } : {}),
      });
    }

    // Honest state (Global Constraint): the essentials write is AWAITED and its
    // failure BLOCKS advancement with an inline retry — previously the error was
    // swallowed (silent) and the user advanced + the "completed" event fired
    // even when nothing persisted (simulated success). Mock/unconfigured mode
    // has no write and proceeds.
    //
    // The row is resolved through `persistEssentials` (membership seam) and the
    // write asserts rows-affected. Targeting by `user_id` here matched ZERO rows
    // for every create-path founder — that column is deliberately NULL until a
    // directory claim sets it — and a zero-row PostgREST update returns
    // `error: null`, so this check passed while nothing was saved. Confirmed on
    // staging 2026-07-20; guarded by
    // src/lib/startup/__tests__/persist-essentials.test.ts.
    if (isSupabaseConfigured() && user) {
      try {
        await persistEssentials(supabase, update);
      } catch (err) {
        setIsSaving(false);
        const { message, isCancelled } = handleError(err, {
          context: 'CompanyEssentials.persistEssentials',
        });
        if (!isCancelled) {
          setSaveError(message);
          showError('Could not save your details', message);
        }
        return;
      }
    }

    // Activation §8 — fired ONLY after the awaited write above succeeds. The
    // detailed `onboarding_company_essentials_completed` event is kept for the
    // existing per-field breakdown; `essentials_done` is the funnel step.
    const essentialsProps = {
      hasStage: stage != null,
      raisingStatus: raisingStatus ?? 'unset',
      hasFundingTarget: parsedFundingTarget != null,
      hasFoundedYear: foundedYear != null,
      hasTeamSize: parsedTeamSize != null,
    };
    track('onboarding_company_essentials_completed', essentialsProps);
    track('essentials_done', essentialsProps);

    setIsSaving(false);
    await advance();
  };

  // Form content (shared between mobile and desktop)
  const formContent = (
    <>
      <View className={isWeb && isDesktop ? 'gap-6' : 'gap-5'}>
        {/* Stage (required) */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Stage <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {STAGES.map((s) => (
              <Pressable
                key={s.value}
                onPress={() => {
                  setStage(s.value);
                  if (errors.stage) setErrors((e) => ({ ...e, stage: false }));
                }}
                accessibilityRole="radio"
                accessibilityLabel={s.label}
                accessibilityState={{ selected: stage === s.value }}
                className="flex-1 py-3 rounded-xl items-center justify-center"
                style={{
                  minWidth: 80,
                  minHeight: 44,
                  backgroundColor: stage === s.value ? colors.primary[600] : colors.surface.primary,
                  borderWidth: 1,
                  borderColor: errors.stage
                    ? colors.red.main
                    : stage === s.value
                      ? colors.primary[600]
                      : colors.border.light,
                }}
              >
                <Text
                  numberOfLines={1}
                  style={{
                    color: stage === s.value ? colors.text.inverse : colors.text.primary,
                    fontWeight: '500',
                    fontSize: 13,
                  }}
                >
                  {s.label}
                </Text>
              </Pressable>
            ))}
          </View>
          {errors.stage && (
            <Text
              style={{ color: colors.red.main }}
              className="text-xs mt-1.5"
              accessibilityRole="alert"
              accessibilityLiveRegion="polite"
            >
              Please select your stage
            </Text>
          )}
        </View>

        {/* Actively raising (required, 3-state) */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Are you fundraising? <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {RAISING_OPTIONS.map((opt) => {
              const selected = raisingStatus === opt.value;
              return (
                <Pressable
                  key={opt.value}
                  onPress={() => {
                    setRaisingStatus(opt.value);
                    if (errors.raising) setErrors((e) => ({ ...e, raising: false }));
                  }}
                  accessibilityRole="radio"
                  accessibilityLabel={opt.label}
                  accessibilityState={{ selected }}
                  className="flex-1 py-3 px-2 rounded-xl items-center justify-center"
                  style={{
                    minWidth: 100,
                    minHeight: 44,
                    backgroundColor: selected ? colors.primary[600] : colors.surface.primary,
                    borderWidth: 1,
                    borderColor: errors.raising
                      ? colors.red.main
                      : selected
                        ? colors.primary[600]
                        : colors.border.light,
                  }}
                >
                  <Text
                    numberOfLines={1}
                    style={{
                      color: selected ? colors.text.inverse : colors.text.primary,
                      fontWeight: '500',
                      fontSize: 13,
                    }}
                  >
                    {opt.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          {errors.raising && (
            <Text
              style={{ color: colors.red.main }}
              className="text-xs mt-1.5"
              accessibilityRole="alert"
              accessibilityLiveRegion="polite"
            >
              Let investors know if you&apos;re raising
            </Text>
          )}
        </View>

        {/* Funding target — only when actively raising */}
        {raisingStatus === 'raising' && (
          <Animated.View entering={entrance(FadeInDown.duration(250))}>
            <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
              Funding Target
            </Text>
            <View
              className="flex-row items-center rounded-xl px-4"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <DollarSign size={18} color={colors.text.tertiary} />
              <TextInput
                style={{
                  flex: 1,
                  paddingVertical: 16,
                  paddingLeft: 6,
                  color: colors.text.primary,
                  fontSize: 16,
                }}
                placeholder="e.g., 500,000"
                placeholderTextColor={colors.text.tertiary}
                value={fundingTargetInput}
                onChangeText={(text) => setFundingTargetInput(formatCurrency(text))}
                keyboardType="number-pad"
                accessibilityLabel="Funding target amount (optional)"
              />
            </View>
            <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1.5">
              Optional — how much you&apos;re currently looking to raise
            </Text>
          </Animated.View>
        )}

        {/* Founded year — optional year picker */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Founded Year
          </Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flexGrow: 0 }}
            contentContainerStyle={{ gap: 8, paddingRight: 8 }}
            keyboardShouldPersistTaps="handled"
          >
            {FOUNDED_YEARS.map((year) => {
              const selected = foundedYear === year;
              return (
                <Pressable
                  key={`year-${year}`}
                  onPress={() => setFoundedYear(selected ? undefined : year)}
                  accessibilityRole="radio"
                  accessibilityLabel={`Founded ${year}`}
                  accessibilityState={{ selected }}
                  className="px-4 py-2.5 rounded-xl items-center justify-center"
                  style={{
                    minHeight: 44,
                    backgroundColor: selected ? colors.primary[600] : colors.surface.primary,
                    borderWidth: 1,
                    borderColor: selected ? colors.primary[600] : colors.border.light,
                  }}
                >
                  <Text
                    numberOfLines={1}
                    style={{
                      color: selected ? colors.text.inverse : colors.text.primary,
                      fontWeight: '500',
                      fontSize: 14,
                    }}
                  >
                    {year}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1.5">
            Optional
          </Text>
        </View>

        {/* Team size — optional */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Team Size
          </Text>
          <View
            className="flex-row items-center rounded-xl px-4"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <Users size={18} color={colors.text.tertiary} />
            <TextInput
              style={{
                flex: 1,
                paddingVertical: 16,
                paddingLeft: 6,
                color: colors.text.primary,
                fontSize: 16,
              }}
              placeholder="e.g., 8"
              placeholderTextColor={colors.text.tertiary}
              value={teamSizeInput}
              onChangeText={(text) => setTeamSizeInput(text.replace(/[^0-9]/g, ''))}
              keyboardType="number-pad"
              accessibilityLabel="Team size (optional)"
            />
          </View>
          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1.5">
            Optional — number of people on your team
          </Text>
        </View>

        {/* Link to full fundraising history */}
        <Pressable
          onPress={() => router.push('/(onboarding)/fundraising-status')}
          accessibilityRole="button"
          accessibilityLabel="Add past fundraising rounds (optional)"
          className="flex-row items-center gap-2 py-2"
          style={{ minHeight: 44 }}
        >
          <TrendingUp size={16} color={colors.primary[600]} />
          <Text style={{ color: colors.primary[600] }} className="text-sm font-medium" numberOfLines={1}>
            Add past fundraising rounds (optional)
          </Text>
        </Pressable>
      </View>

      {/* Continue */}
      <View className="mt-8">
        {saveError && (
          <Text
            style={{ color: colors.red.main }}
            className="text-sm mb-3 text-center"
            numberOfLines={3}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
          >
            {saveError}
          </Text>
        )}
        <AnimatedPressable
          onPress={handleContinue}
          disabled={isSaving}
          accessibilityRole="button"
          accessibilityLabel={saveError ? 'Retry saving your details' : 'Continue'}
          accessibilityState={{ disabled: isSaving, busy: isSaving }}
          className="rounded-xl py-4 flex-row items-center justify-center gap-2"
          style={{ minHeight: 44, backgroundColor: colors.primary[600], opacity: isSaving ? 0.7 : 1 }}
        >
          {isSaving ? (
            <ActivityIndicator size="small" color={colors.text.inverse} />
          ) : (
            <>
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Continue
              </Text>
              <ArrowRight size={20} color={colors.text.inverse} />
            </>
          )}
        </AnimatedPressable>
      </View>
    </>
  );

  // Desktop layout with OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        currentStep="essentials"
        steps={STARTUP_ONBOARDING_DISPLAY_STEPS}
        title="Company Essentials"
        subtitle="Stage and fundraising — helps investors find you"
        userType="startup"
        entityName={startupProfile?.name || undefined}
      >
        {formContent}
      </OnboardingLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          {/* Header */}
          <Animated.View
            entering={entrance(FadeInDown.delay(100).duration(500))}
            className="px-5 pt-4 pb-4"
          >
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => router.back()}
                accessibilityRole="button"
                accessibilityLabel="Go back"
                className="w-11 h-11 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <OnboardingSignOut />
            </View>
          </Animated.View>

          {/* Title */}
          <Animated.View
            entering={entrance(FadeInDown.delay(200).duration(500))}
            className="px-5 pb-6"
          >
            <View className="flex-row items-center gap-3 mb-2">
              <Layers size={24} color={colors.primary[600]} />
              <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                Company Essentials
              </Text>
            </View>
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
              Stage &amp; fundraising
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              Confirm the basics investors filter by
            </Text>
          </Animated.View>

          {/* Form */}
          <ScrollView
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <Animated.View entering={entrance(FadeInUp.delay(300).duration(500))} className="pb-8">
              {formContent}
            </Animated.View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}
